import { z } from "zod";
import { Id } from "../common/ids.js";
import { LocalizedText } from "../common/localized-text.js";
import { Money } from "../common/money.js";
import { Timestamp } from "../common/time.js";
import { defineRoute } from "../http/route.js";
import { AftersaleCaseView, AftersaleDecision } from "./case.routes.js";
/**
 * The dispute desk (AFS-3, ADM-2, cards 089 and 090): escalated cases for resolvers, oldest first. A resolver takes a
 * case and decides it with reasons both parties see; an appeal goes to a different resolver, whose decision is final.
 */
const Ops = { surface: "operator", auth: "operator", permission: "ops:dispute.resolve" };
const CaseId = z.object({ caseId: Id });
const Party = z.object({ id: Id, name: LocalizedText });
export const OpsDisputeSummary = z
    .object({
    id: Id,
    orderId: Id,
    orderNumber: z.string(),
    type: AftersaleCaseView.shape.type,
    status: AftersaleCaseView.shape.status,
    requested: Money.nullable(),
    buyer: Party,
    seller: Party,
    escalatedAt: Timestamp.nullable(),
    evidenceDueAt: Timestamp.nullable(),
    appeal: z.boolean().describe("An appeal waits for a second resolver"),
    assigneeOperatorId: Id.nullable(),
})
    .meta({ id: "OpsDisputeSummary" });
export const OpsDispute = AftersaleCaseView.omit({ side: true, actions: true })
    .extend({
    buyer: Party,
    seller: Party,
    assigneeOperatorId: Id.nullable(),
    conversationId: Id.nullable().describe("The order's conversation, for support's message reader under a grant"),
    files: z
        .array(z.object({ fileId: Id, name: z.string(), url: z.string() }))
        .describe("Five-minute links to the evidence files and settlement proof, clean ones only"),
    decisions: z.array(AftersaleDecision.extend({ operatorId: Id })).describe("Every decision, oldest first"),
    firstResolverOperatorId: Id.nullable().describe("Who decided first, and so cannot hear the appeal"),
})
    .meta({ id: "OpsDispute" });
export const listDisputes = defineRoute({
    ...Ops,
    id: "ops.disputes.list",
    method: "GET",
    path: "/v1/ops/disputes",
    marks: [],
    summary: "Disputes, oldest first: open ones (escalated or under review, appeals included) or decided ones",
    prd: ["AFS-3", "ADM-2"],
    request: {
        query: z.object({
            state: z.enum(["open", "decided"]).default("open"),
            assignee: z.enum(["any", "me", "unassigned"]).default("any"),
        }),
    },
    responses: { 200: { description: "Disputes", schema: z.object({ items: z.array(OpsDisputeSummary) }) } },
    errors: ["AUTH_REQUIRED", "PERMISSION_DENIED", "VALIDATION_FAILED"],
});
export const getDispute = defineRoute({
    ...Ops,
    id: "ops.disputes.get",
    method: "GET",
    path: "/v1/ops/disputes/{caseId}",
    marks: [],
    summary: "A dispute with both sides' evidence, the order's parties, the timeline, and every decision",
    prd: ["AFS-3", "ADM-2"],
    request: { params: CaseId },
    responses: { 200: { description: "The dispute", schema: OpsDispute } },
    errors: ["AUTH_REQUIRED", "PERMISSION_DENIED", "NOT_FOUND"],
});
export const assignDispute = defineRoute({
    ...Ops,
    id: "ops.disputes.assign",
    method: "POST",
    path: "/v1/ops/disputes/{caseId}/assign",
    marks: [],
    summary: "Take an escalated case, or give it to another resolver, which starts its review; an appeal goes only to a resolver with `ops:appeal.resolve` who did not decide it first",
    prd: ["AFS-3", "ADM-2"],
    request: {
        params: CaseId,
        body: z.object({ operatorId: Id.optional().describe("Defaults to the caller") }),
    },
    responses: { 200: { description: "The dispute", schema: OpsDispute } },
    errors: ["AUTH_REQUIRED", "PERMISSION_DENIED", "NOT_FOUND", "INVALID_TRANSITION"],
});
export const decideDispute = defineRoute({
    ...Ops,
    id: "ops.disputes.decide",
    method: "POST",
    path: "/v1/ops/disputes/{caseId}/decide",
    marks: [],
    summary: "The assigned resolver decides: grant what was asked, a partial amount below it (not for a replacement), or reject, with reasons of at least 20 characters that both parties see. The amount never exceeds what the buyer paid. A first decision waits until the evidence period ends (`INVALID_TRANSITION` before it). A first decision may be appealed for the RuleSet's appeal days, then the worker carries it out; an appeal's decision is final",
    prd: ["AFS-3"],
    request: {
        params: CaseId,
        body: z.object({
            outcome: z.enum(["grant", "partial", "reject"]),
            amount: Money.optional().describe("Required for a partial decision"),
            reasons: z.string().trim().min(20).max(4000),
        }),
    },
    responses: { 200: { description: "The dispute", schema: OpsDispute } },
    errors: ["AUTH_REQUIRED", "PERMISSION_DENIED", "NOT_FOUND", "VALIDATION_FAILED", "INVALID_TRANSITION"],
});
export const disputeRoutes = [listDisputes, getDispute, assignDispute, decideDispute];
//# sourceMappingURL=dispute.routes.js.map